import {
  onMounted,
  h,
  ref,
  computed,
  nextTick,
  watch,
} from 'vue';
import { brand } from 'config';
import {
  getZipLocationInfo,
  getLocationsInfo,
  getStoreInventoryInfo,
  getZipCodeByLocation,
  getZipCode,
  getPickupStore,
  copyFacet,
  findFacetOption,
  isLoading,
} from '../utils/deliveryAndPickupUtil';
import useLocatorAndZipSlideouts from './useLocatorAndZipSlideouts';
import useFacets from './useFacets';

const zipCodeSlideoutId = 'ff';
const locatorSlideoutId = 'locator-slideout-filtering-facets';

// --- STATELESS UTILS ---

const applyFunctionsToOptions = (facet, callbacksFuncs) => {
  const callbacks = [callbacksFuncs];

  ['SDD', 'BOPS'].forEach((type) => {
    const option = findFacetOption(facet, type);
    /* istanbul ignore next */
    if (option !== undefined && callbacks) {
      callbacks.forEach((cb) => cb(option, type));
    }
  });
  return facet;
};

// updates the value fields of given options (option type can be SDD or BOPS) of the facet
const setOptionValues = (facet, options) => {
  const selectedValues = new Set(facet.selectedValues);
  let selectionIsUpdated = false;

  applyFunctionsToOptions(facet, (option, type) => {
    /* istanbul ignore next */
    if (options[type] !== undefined) {
      const newValue = options[type] ? `${type}=${options[type]}` : `${type}=`;
      /* istanbul ignore next */
      if (newValue !== option.value) {
        selectionIsUpdated = selectedValues.has(option.value);
        /* istanbul ignore next */
        if (selectionIsUpdated) {
          // replacing older selected value with the new value if the option is selected
          selectedValues.delete(option.value);
          selectedValues.add(newValue);
        }
        option.value = newValue;
      }
    }
  });

  facet.selectedValues = [...selectedValues];

  return selectionIsUpdated;
};

const renderDnFLinkButton = (type, text) => (
  `<a href="javascript:void(window.dispatchEvent(new Event('filtering-facets-change-${type}')))">${text}</a>` // It needs to be refactored.
);

/* istanbul ignore next */
const renderSddText = (sddMessage, zipCode) => (
  `${sddMessage || 'Same-day Delivery'} to ${renderDnFLinkButton('sdd', zipCode || 'Enter Zip')}`
);

/* istanbul ignore next */
const renderBopsText = (locationName, curbsideString) => (
  `<div class="upc-bops-value-title">Pickup at ${renderDnFLinkButton('bops', locationName || 'Shop near you')}</div> ${curbsideString}`
);

const getValueType = (value) => value.substring(0, value.indexOf('=')).toUpperCase();

export default function useDeliveryAndPickup(props, msdRef) {
  const state = {
    sddZipCode: ref(null),
    bopsStore: ref(null),
    eligibility: ref({}),
    locations: ref({}),
    updatesCounter: 0, // triggers the facet rerender
  };

  const {
    globalOptions,
    facetByName,
  } = useFacets();

  // rises the facets update event for the filtering-facets consumer
  const applyUpdatedFacet = () => msdRef.value.updateCheckboxCriteria();

  // --- LOCATIONS ---

  const getLocation = (locations, locationNumber, def = { sddMessage: 'Same-day Delivery', name: 'Shop near you' }) => (
    locations[locationNumber] || def
  );
  const setLocation = (locationNumber, location) => { state.locations.value[locationNumber] = location; };

  // --- ELIGIBILITY ---

  const getLocationEligibility = (eligibility, locationNumber, def = { sddEligible: false, bopsEligible: false, curbsidePickupEligible: false }) => (
    eligibility[locationNumber] || def
  );
  const setLocationEligibility = (locationNumber, eligibility) => { state.eligibility.value[locationNumber] = eligibility; };

  // --- NETWORK UTILS ---

  const loadSddZipCodeInfo = async (zipCode, oldZipcode) => {
    /* istanbul ignore next */
    if (zipCode === oldZipcode) { return -1; }
    try {
      const locationNumber = await getZipLocationInfo(zipCode);
      return locationNumber;
    } catch (error) {
      console.error('Error to load zip code information:', error); // eslint-disable-line no-console
      return null;
    }
  };

  const loadEligibility = async (locationNumbers) => {
    const inventory = await getStoreInventoryInfo(locationNumbers);
    /* istanbul ignore next */
    if (inventory) {
      inventory.forEach((eligibility) => setLocationEligibility(eligibility.locationNumber, eligibility));
    }
    return inventory;
  };

  const loadLocationsInfo = async (ids) => {
    const locations = await getLocationsInfo(ids);
    /* istanbul ignore next */
    if (locations) {
      locations.forEach((location) => setLocation(location.locationNumber, location));
    }
    return locations;
  };

  // --- UPDATE FACET ---

  const updateOption = (type, locationNumber) => {
    /* istanbul ignore next */
    if (locationNumber) {
      loadEligibility([locationNumber]);
      loadLocationsInfo([locationNumber]);
    }
    const facet = copyFacet(state.facet.value);
    const selectionIsUpdated = setOptionValues(facet, { [type]: locationNumber });
    state.facet.value = facet;
    /* istanbul ignore next */
    if (selectionIsUpdated) {
      facet.selectedValues = facet.selectedValues.filter((ele) => ele !== 'SDD=' && ele !== 'BOPS=');
      nextTick().then(applyUpdatedFacet);
    }
  };

  const updateSddZipCode = async (zipCode, sddLocationNumber) => {
    let locationNumber = sddLocationNumber;
    /* istanbul ignore next */
    if (zipCode) {
      /* istanbul ignore next */
      if (!locationNumber) {
        locationNumber = await loadSddZipCodeInfo(zipCode, state.sddZipCode.value);
      }
      /* istanbul ignore next */
      if (locationNumber !== -1) {
        state.sddZipCode.value = zipCode;
        /* istanbul ignore next */
        updateOption('SDD', locationNumber);
      }
    }

    return locationNumber;
  };

  const updateBopsLocation = async (locationNumber) => {
    /* istanbul ignore next */
    updateOption('BOPS', locationNumber);
  };

  function customValueRenderer({ value, label }) {
    const valueType = getValueType(value);
    /* istanbul ignore next */
    const itemText = state.text[valueType]?.value || label || value;
    const disabled = state.disabled[valueType]?.value;
    const containsNumber = /\d/;
    let val = value;
    if (valueType === 'SDD' && state.sddLocationNumber.value && !containsNumber.test(val)) {
      val += state.sddLocationNumber.value;
    } else if (valueType === 'BOPS' && state.bopsLocationNumber.value && !containsNumber.test(val)) {
      val += state.bopsLocationNumber.value;
    }

    const rendered = {
      label: h('div', { class: `upc-${valueType.toLowerCase()}-value`, innerHTML: itemText }),
      ariaLabel: itemText,
      value: val,
      labelType: 'html',
      disabled,
    };

    return rendered;
  }

  // --- FACET STATE ---
  /* istanbul ignore next */
  Object.assign(state, {
    facet: ref(copyFacet(props.facet)),
    sddLocationNumber: computed(() => {
      const optionVal = findFacetOption(state.facet.value, 'SDD');
      /* istanbul ignore next */
      return (optionVal?.value) ? Number(optionVal.value.substring(4)) : 0;
    }),
    bopsLocationNumber: computed(() => {
      const bopsFacetOptions = findFacetOption(state.facet.value, 'BOPS');
      /* istanbul ignore next */
      return (bopsFacetOptions?.value) ? Number(bopsFacetOptions.value.substring(5)) : 0;
    }),
    sddEligibility: computed(() => getLocationEligibility(state.eligibility.value, state.sddLocationNumber.value)),
    bopsEligibility: computed(() => getLocationEligibility(state.eligibility.value, state.bopsLocationNumber.value)),
    isSddEligible: computed(() => state.sddEligibility.value?.sddEligible || false),
    isBopsEligible: computed(() => state.bopsEligibility.value?.bopsEligible || false),
    isCurbsideEligible: computed(() => state.bopsEligibility.value?.curbsidePickupEligible || false),
    curbsideString: computed(() => (state.isCurbsideEligible.value ? '<div class="upc-bops-value-curbside">Curbside pickup available</div>' : '')),
    sddLocation: computed(() => getLocation(state.locations.value, state.sddLocationNumber.value)),
    bopsLocation: computed(() => getLocation(state.locations.value, state.bopsLocationNumber.value)),
    sddMessage: computed(() => state.sddLocation.value?.sddMessage),
    bopsLocationName: computed(() => state.bopsLocation.value?.name || ''),
    bopsStoreZip: computed(() => state.bopsLocation.value?.address?.zipCode || 0),
    sddZip: computed(() => state.sddZipCode.value || ''),
    text: {
      SDD: computed(() => renderSddText(state.sddMessage.value, state.sddZipCode.value)),
      BOPS: computed(() => renderBopsText(state.bopsLocationName.value, state.curbsideString.value)),
    },
    disabled: {
      SDD: computed(() => !state.isSddEligible.value || isLoading.value),
      BOPS: computed(() => !state.isBopsEligible.value || isLoading.value),
    },
    selected: {
      SDD: computed(() => !state.disabled.SDD.value && state.facet.value.selectedValues.find((v) => v.includes('SDD=') && v.length > 4)),
      BOPS: computed(() => !state.disabled.BOPS.value && state.facet.value.selectedValues.find((v) => v.includes('BOPS=' && v.length > 5))),
    },
  });

  /* istanbul ignore next */
  const locatorData = {
    locationType: (brand === 'mcom') ? 'MSTORE' : 'BSTORE',
    divisionNumber: (brand === 'mcom') ? 12 : 13,
    myStoreId: state.bopsLocationNumber,
    resultsDisplayed: 5,
    locationRadius: 100,
    useFccApi: false,
    showAccordion: false,
    showCurbsideHours: false,
    showAvailability: false,
    zipCode: state.bopsStoreZip,
    address: '',
    isBridal: false,
    useMspLocatorApi: false,
    searchByZipCode: false,
  };

  // --- EVENT HANDLERS ---

  const reloadFacetSelectedValues = () => {
    const facet = facetByName(state.facet.value.name);
    state.facet.value.selectedValues = [...facet?.selectedValues];
  };

  watch(() => props.facet, () => {
    /* istanbul ignore next */
    nextTick(reloadFacetSelectedValues);
  });

  const onSelectionStateUpdate = (selected) => {
    nextTick(reloadFacetSelectedValues);
    return selected;
  };
  const onBopsUpdate = (locationNumber, zipCode) => {
    updateBopsLocation(locationNumber || state.bopsLocationNumber.value || getPickupStore(), zipCode);
  };

  const onSddUpdate = async (zip) => {
    /* istanbul ignore next */
    const sddLN = zip ? undefined : state.sddLocationNumber.value;
    /* istanbul ignore next */
    const zipCode = zip || (sddLN ? await getZipCodeByLocation(sddLN) : getZipCode());
    updateSddZipCode(zipCode, sddLN);
  };

  const onGlobalStoreChange = () => {
    onBopsUpdate(getPickupStore());
  };

  const initFacet = async () => {
    onSddUpdate();
    onBopsUpdate();
  };

  const slideouts = useLocatorAndZipSlideouts({
    onBopsUpdate,
    onSddUpdate,
    onGlobalStoreChange,
  });

  onMounted(initFacet);

  /* istanbul ignore next */
  function onParentInit() {
  }

  return {
    onParentInit,
    dnpState: state,
    locatorData,
    onSelectionStateUpdate,
    customValueRenderer,
    zipCodeSlideoutId,
    locatorSlideoutId,
    pageType: globalOptions.pageType,
    ...slideouts,
  };
}
